import cx from 'classnames'
import { useFeature } from '@growthbook/growthbook-react'
import { isNil } from 'lodash'
import { useRef, Fragment, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { isDev } from 'config'

import { selectIsLoggedIn } from 'store/selectors'
import { Link } from 'features/i18n/router'
import { Group, Post, PostFeedback } from 'lib/_types'
import { ROUTE_PATH } from 'lib/paths'
import { useLinger } from 'hooks'
import { useCurrentUser } from 'store/api/yay/user'
import { useRespondRecommendedPostMutation } from 'store/api/yay/post'
import {
  groupToGroupContextSchema,
  postToPostContextSchema,
  snowplowEvent,
  userToUserContextSchemaData,
} from 'lib/analytics'
import {
  usePostLink,
  useScrollToPost,
  useTrackPostViewEvent,
} from './PostItem.hooks'

import Button from 'components/Button'
import ReplyTo from 'components/ReplyTo'
import Pinned from 'components/Pinned'
import PostContent from '../PostContent'
import PostActions from '../PostActions'
import PostMenu from '../PostMenu'
import PostAvatar from './PostAvatar'
import PostUserName from './PostUserName'
import PostTime from './PostTime'
import { PostBreadcrumb } from './PostBreadcrumb'

import { ReactComponent as IconThumbsUp } from 'icons/PostList__item__feedback__a--tu.svg'
import { ReactComponent as IconThumbsDown } from 'icons/PostList__item__feedback__a--td.svg'
import { ReactComponent as IconLink } from 'icons/PostList__item__feedback__a--link.svg'

const PostItem = ({
  post,
  isConversationLayout,
  isConversationLayoutSmall,
  group,
  isLast,
  isShared = false,
  showPostAction = true,
  showPostMenu = true,
  showFeedback = false,
  noLink = false,
  showBreadcrumb = false,
  onReply,
  onRepost,
  onToggleLike,
  onPinPost,
  onUnpinPost,
  onVote,
  onDeletePost,
  onBlockUser,
  onHighlight,
  onUnHighlight,
  onBookmark,
  onUnBookmark,
  onBanUser,
  onMuteUser,
  onClick,
  isHot = false,
  hideSingleMention,
  hasSeeMore,
  showReposts = false,
  enableViewportCheck,
  index,
  highlight,
  giveFeedback,
  isLoadingFeedback,
  isFromTimeline = false,
  onSavePreloadedPosts,
}: {
  post: Post
  isConversationLayout?: boolean
  isConversationLayoutSmall?: boolean
  group?: {
    data?: Group
  }
  showPostAction?: boolean
  showPostMenu?: boolean
  showFeedback?: boolean
  isLast?: boolean
  noLink?: boolean
  showBreadcrumb?: boolean
  onReply?: any
  onRepost?: any
  onToggleLike?: any
  onPinPost?: Function
  onUnpinPost?: Function
  onVote?: Function
  onDeletePost?: Function
  onBlockUser?: Function
  onHighlight?: Function
  onUnHighlight?: Function
  onBanUser?: Function
  onMuteUser?: Function
  onBookmark?: Function
  onUnBookmark?: Function
  onClick?: Function
  isShared?: boolean
  isHot?: boolean
  hideSingleMention?: boolean
  hasSeeMore?: boolean
  showReposts?: boolean
  enableViewportCheck?: boolean
  index?: number
  highlight?: string
  giveFeedback?: ({
    post,
    feedback,
  }: {
    post: Post
    feedback: PostFeedback
  }) => void
  isLoadingFeedback?: boolean
  isFromTimeline?: boolean
  onSavePreloadedPosts?: Function
}) => {
  const navigate = useNavigate()

  const isLoggedIn = useSelector(selectIsLoggedIn)
  const currentUserId = useCurrentUser()?.data?.id
  const isReplyPost = typeof post?.in_reply_to_post !== 'undefined'
  const Handler = (noLink ? 'a' : Link) as any
  const isGroupPost = post.group_id !== null
  const { t } = useTranslation()
  const rootRef = useRef<HTMLDivElement>()

  const [__isCopied, __isCopiedSet] = useState(false)
  const ___copyTO = useRef<null | number>()
  const ___$wrapper = useRef<HTMLDivElement>()

  const { postLink, linkRoute } = usePostLink({ noLink, post })

  const [respondRecommendedPostReq, respondRecommendedPostRes] =
    useRespondRecommendedPostMutation()
  const [recommendedPostVisible, recommendedPostVisibleSet] = useState(true)

  useScrollToPost({ post, rootRef })
  useTrackPostViewEvent({ post, rootRef })

  const growthBookExperiment = useFeature<{
    en: number
    number: number
    visible: boolean
    vn: number
  }>('yd-115')
  const isRecommendLabelVisible = useFeature('yay-13059')?.value

  const context = {
    post: postToPostContextSchema({
      post,
      position: index >= 0 ? index + 1 : undefined,
      location: group?.data ? 'group' : 'timeline',
    }),
    group: groupToGroupContextSchema({
      group: group?.data,
      currentUserId,
    }),
  }

  const { inViewRef } = useLinger({
    skip: !enableViewportCheck,
    enterCallback: (params) => {
      if (params.first_impression) {
        snowplowEvent.impression(context)
      }
    },
    leaveCallback: (params) => {
      snowplowEvent.linger({ params, ...context })
    },
  })

  const handleHidePost = (feedbackType: 'hide' | 'undo') => {
    respondRecommendedPostReq({
      userId: currentUserId,
      postId: post.id,
      en: growthBookExperiment?.value?.en,
      vn: growthBookExperiment?.value?.vn,
      feedbackType: feedbackType,
    })
    recommendedPostVisibleSet((prev) => !prev)
  }

  // We should be able to trust BE on `is_muted`. But here's a guard anyway.
  // Group owner should always see the mute tag.
  // Group mods should see the mute tag, unless it's themselves who got muted.
  // https://jira.nanameue.jp/browse/YAY-15810.
  // thread post not implemented yet
  const isMuted =
    post?.is_muted &&
    !post?.thread_id &&
    (group?.data?.owner?.id === currentUserId ||
      (group?.data?.moderator_ids?.includes(currentUserId) &&
        post?.user?.id != currentUserId))

  useEffect(() => {
    if (respondRecommendedPostRes.isError) {
      // revert to original visibility because request for toggle did not go through
      recommendedPostVisibleSet((prev) => !prev)
    }
  }, [respondRecommendedPostRes])

  return (
    <div
      id={post.id ? `post-${post.id}` : null}
      ref={(ref) => {
        rootRef.current = ref
        inViewRef(ref)
      }}
      className={cx('PostList__item', {
        'PostList__item--conversation': isConversationLayout,
        'PostList__item--last': isLast,
      })}
    >
      {isRecommendLabelVisible && post?._isRecommended >= 0 && (
        <>
          {recommendedPostVisible && (
            <div className="PostList__item__header">
              <div className="PostList__item__header__left">
                <img src="/images/icon-search-recent-new.svg" />
                {t('recommended.recommended_for_you')}
              </div>
              <div
                className="PostList__item__header__right"
                onClick={() => handleHidePost('hide')}
              >
                <img src="/images/icon-close-gray.svg" />
              </div>
            </div>
          )}
          {!recommendedPostVisible && (
            <div className="PostList__item__hidden" ref={___$wrapper}>
              <div className="PostList__item__hidden__content">
                {t('recommended.post_hidden')}
                <div className="PostList__item__hidden__action">
                  <Button
                    buttonStyle="text"
                    size="small"
                    onClick={() => handleHidePost('undo')}
                  >
                    {t('common.undo')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {(isNil(post?._isRecommended) ||
        (post?._isRecommended >= 0 && recommendedPostVisible)) && (
        <div className="PostList__item__wrapper" ref={___$wrapper}>
          <Handler
            className="PostList__item__handle"
            onClick={(e: Event) => {
              e.preventDefault() // prevent race conditions with to attribute
              e.stopPropagation()

              // the current location is the main timeline / or a circle timeline /group/GROUP_ID
              if (isFromTimeline && onSavePreloadedPosts) {
                console.log('PostItem: requesting save preloaded posts')
                onSavePreloadedPosts()
              }

              if (
                (
                  [
                    ROUTE_PATH.CONVERSATION,
                    ROUTE_PATH.GROUP_CONVERSATION,
                    ROUTE_PATH.GROUP_THREAD_CONVERSATION,
                  ] as (string | Function)[]
                ).includes(linkRoute)
              ) {
                snowplowEvent.see_conversation(context)
              }

              onClick?.(post, e)
              navigate(postLink)
            }}
            draggable={false}
          />

          {post?._deleted ? (
            <div className="PostList__item__deleted">
              {t('reply_to.post_reply_target_null')}
            </div>
          ) : (
            <Fragment>
              {post._pinned && !isShared && <Pinned text={t('post.pinned')} />}

              {showBreadcrumb && <PostBreadcrumb post={post} />}

              <PostAvatar
                user={post?.user}
                isConversationLayoutSmall={isConversationLayoutSmall}
                isGroupPost={isGroupPost}
                onClick={() => {
                  snowplowEvent.open_profile({
                    user: userToUserContextSchemaData({ user: post?.user }),
                    ...context,
                  })
                }}
              />

              <div className="PostList__item__center">
                <div className="PostList__item__center__top-left">
                  <PostUserName
                    user={post?.user}
                    isHot={isHot}
                    isGroupPost={isGroupPost}
                    isGroupOwner={group?.data?.owner?.id === post?.user?.id}
                    isGroupModerator={
                      post?.user?.group_user?.is_moderator ||
                      group?.data?.moderator_ids?.includes(post?.user?.id)
                    }
                  />

                  {isReplyPost && (
                    <ReplyTo
                      post={post}
                      repliedPost={post?.in_reply_to_post}
                      postLink={postLink}
                      hideSingleMention={hideSingleMention}
                      noLink={noLink}
                      isFromTimeline={isFromTimeline}
                      onSavePreloadedPosts={onSavePreloadedPosts}
                    />
                  )}
                </div>

                <div className="PostList__item__center__top-right">
                  {!isRecommendLabelVisible &&
                    isDev &&
                    post?._isRecommended >= 0 && (
                      <p
                        className="PostList__item__recommended"
                        title="for testing only"
                      >
                        Recommended #{post?._isRecommended + 1}
                      </p>
                    )}

                  <PostTime createdAt={post?.created_at} />
                </div>

                {isMuted && (
                  <div className="PostList__item__mute">{t('post.muted')}</div>
                )}

                <PostContent
                  post={post}
                  group={group}
                  isShared={isShared}
                  onVote={onVote}
                  postLink={postLink}
                  highlight={highlight}
                  isFromTimeline={isFromTimeline}
                  onSavePreloadedPosts={onSavePreloadedPosts}
                />

                {showReposts && post?.reposts_count > 0 && (
                  <div className="PostList__item__reposts">
                    <Link
                      to={
                        isGroupPost
                          ? ROUTE_PATH.GROUP_POST_REPOST(post.group_id, post.id)
                          : ROUTE_PATH.POST_REPOST(post.id)
                      }
                      className="PostList__item__reposts__a"
                    >
                      {t('post.post_repost_count', {
                        reposts: post?.reposts_count,
                      })}
                    </Link>
                  </div>
                )}

                <div className="PostList__item__footer">
                  {showPostAction && (
                    <PostActions
                      onToggleLike={onToggleLike}
                      onRepost={onRepost}
                      onReply={onReply}
                      post={post}
                      isMuted={isMuted}
                    />
                  )}

                  {isLoggedIn && showPostMenu && (
                    <PostMenu
                      post={post}
                      index={index}
                      group={group}
                      isGroupPost={isGroupPost}
                      onPinPost={onPinPost}
                      onUnpinPost={onUnpinPost}
                      onDeletePost={onDeletePost}
                      onBlockUser={onBlockUser}
                      onHighlight={onHighlight}
                      onUnHighlight={onUnHighlight}
                      onBookmark={onBookmark}
                      onUnBookmark={onUnBookmark}
                      onBanUser={onBanUser}
                      onMuteUser={onMuteUser}
                      isMuted={isMuted}
                    />
                  )}
                </div>

                {showFeedback && (
                  <div className="PostList__item__feedback">
                    <div className="PostList__item__feedback__actions">
                      <a
                        className={cx('PostList__item__feedback__a', {
                          'PostList__item__feedback__a--x-disabled':
                            isLoadingFeedback,
                          'PostList__item__feedback__a--active':
                            post?._feedback == 'good',
                        })}
                        onClick={() => {
                          giveFeedback({ post, feedback: 'good' })
                        }}
                      >
                        <IconThumbsUp />
                      </a>

                      <a
                        className={cx('PostList__item__feedback__a', {
                          'PostList__item__feedback__a--x-disabled':
                            isLoadingFeedback,
                          'PostList__item__feedback__a--active':
                            post?._feedback == 'bad',
                          'PostList__item__feedback__a--td': true,
                        })}
                        onClick={() => {
                          giveFeedback({ post, feedback: 'bad' })
                        }}
                      >
                        <IconThumbsDown />
                      </a>
                    </div>

                    <a
                      className={cx('PostList__item__feedback__a', {
                        'PostList__item__feedback__a--link': true,
                        'PostList__item__feedback__a--x-disabled': __isCopied,
                      })}
                      onClick={() => {
                        navigator?.clipboard
                          ?.writeText?.(
                            (
                              ___$wrapper.current?.querySelector?.(
                                '.PostList__item__handle'
                              ) as HTMLAnchorElement
                            )?.href
                          )
                          .then(() => {
                            __isCopiedSet(true)

                            window.clearTimeout(___copyTO.current)
                            ___copyTO.current = window.setTimeout(() => {
                              __isCopiedSet(false)
                            }, 1000)
                          })
                      }}
                    >
                      <IconLink />
                    </a>
                  </div>
                )}
              </div>
            </Fragment>
          )}

          {hasSeeMore && (
            <a className="PostList__item__see-more">{t('common.see_more')}</a>
          )}
        </div>
      )}
    </div>
  )
}

export default PostItem
